import { useTranslation } from "react-i18next";
import { Button } from "react-aria-components";
import { cn } from "@core/utils";
import { Icon } from "@core/assets";

interface PaginationProps {
  /** 1-based current page. */
  page: number;
  /** Total page count; the control renders nothing when there is one page or fewer. */
  pageCount: number;
  onPageChange: (page: number) => void;
  /** Disables every control while the list it drives is fetching. */
  isDisabled?: boolean;
}

/** Sentinel for a collapsed page range — rendered as an inert "…". */
const GAP = "gap" as const;

/**
 * Build the page list with at most one collapsed run on each side, e.g.
 * `1 2 3 … 9 10` — the design's shape. Always keeps the first page, the last page, and the
 * current page with one neighbour, so the control's width stays stable as pages change.
 */
function pageItems(page: number, pageCount: number): (number | typeof GAP)[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const items: (number | typeof GAP)[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);

  if (start > 2) items.push(GAP);
  for (let n = start; n <= end; n += 1) items.push(n);
  if (end < pageCount - 1) items.push(GAP);
  items.push(pageCount);

  return items;
}

/**
 * List pagination (own-the-code). Page numbers plus previous/next, sitting
 * under a top border as in the design.
 *
 * The arrow glyphs are physically directional, so they get `` — the ONE place a
 * physical direction is correct, because "previous" points at the start of the reading order,
 * Previous points left, next points right.
 */
export function Pagination({
  page,
  pageCount,
  onPageChange,
  isDisabled = false,
}: PaginationProps) {
  const { t } = useTranslation("common");

  if (pageCount <= 1) return null;

  const navClass =
    "flex items-center gap-xs rounded-md p-1.5 text-sm-semibold text-(--text-secondary) outline-none " +
    "transition-colors hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-focus-ring " +
    "disabled:cursor-not-allowed disabled:text-(--text-disabled) disabled:hover:bg-transparent";

  return (
    <nav
      aria-label={t("pagination.label")}
      className="flex w-full flex-wrap items-center justify-between gap-xs border-t border-(--border-default) pt-md"
    >
      <Button
        onPress={() => onPageChange(page - 1)}
        isDisabled={isDisabled || page <= 1}
        className={navClass}
      >
        <Icon name="arrow-left" size={20} />
        <span className="max-sm:sr-only">{t("pagination.previous")}</span>
      </Button>

      {/* Below sm the numbered list cannot fit: seven 40px targets plus both nav buttons need
          ~460px, and 320px is the floor. A position readout replaces it — prev/next still do
          the navigating, and their labels collapse to arrows at the same breakpoint. */}
      <p className="text-sm-medium text-(--text-secondary) sm:hidden">
        {t("pagination.position", { page, pageCount })}
      </p>

      <ol className="hidden items-center gap-0.5 sm:flex">
        {pageItems(page, pageCount).map((item, index) =>
          item === GAP ? (
            <li
              // Gaps carry no identity of their own; position is the only stable key, and the
              // list is fully re-derived from `page`/`pageCount` on every render.
              key={`gap-${String(index)}`}
              aria-hidden
              className="grid size-10 place-items-center text-sm-medium text-(--text-secondary)"
            >
              …
            </li>
          ) : (
            <li key={item}>
              <Button
                onPress={() => onPageChange(item)}
                isDisabled={isDisabled}
                aria-label={t("pagination.go-to-page", { page: item })}
                aria-current={item === page ? "page" : undefined}
                className={cn(
                  "grid size-10 place-items-center rounded-md outline-none transition-colors",
                  "focus-visible:ring-2 focus-visible:ring-focus-ring",
                  item === page
                    ? "bg-gray-50 text-sm-semibold text-(--text-primary)"
                    : "text-sm-medium text-(--text-secondary) hover:bg-gray-50"
                )}
              >
                {item}
              </Button>
            </li>
          )
        )}
      </ol>

      <Button
        onPress={() => onPageChange(page + 1)}
        isDisabled={isDisabled || page >= pageCount}
        className={navClass}
      >
        <span className="max-sm:sr-only">{t("pagination.next")}</span>
        <Icon name="arrow-right" size={20} />
      </Button>
    </nav>
  );
}

export type { PaginationProps };
